/**
 * `/v1/festivals` — the endpoint the sidebar has been missing since Phase 0.
 *
 * ---------------------------------------------------------------------------------------------
 * FIVE CALLS SINCE QA-RUNDE 12 (#28). This paragraph said there was deliberately no `DELETE`,
 * because decision 0010 left what removing a festival should mean undecided. Atle decided:
 * faresone nederst i «Rediger», rød knapp, and a confirmation that lists what goes with it and
 * only accepts the festival's own name typed out. The server soft-deletes the festival and every
 * year under it; nothing here offers an undo, because there is no restore route to honour one
 * with — re-creating a festival by the same name restores the row and its id, and that is the
 * whole of the recovery that exists today.
 *
 * THE LIST IS REFERENCE TIER, NOT LIST TIER. It is the sidebar, on every page, for the whole
 * session — an organisation adds a festival a few times a year and then looks at the same three
 * names for twelve hours. `CACHE.list`'s 30-second staleness would put a request on the wire on
 * every navigation for an answer that has not moved since spring.
 *
 * ONE PAGE, AND THAT IS THE WHOLE LIST. `MAX_PAGE_SIZE` is 100 and the sidebar is a menu: an
 * organisation with a hundred festivals has a navigation problem that a "load more" button at
 * the foot of the sidebar does not solve. `hasMore` is therefore not paged through here — if it
 * is ever true, the answer is a real festival page with a search box, not a second request.
 * ---------------------------------------------------------------------------------------------
 */
import {
  festivalDtoSchema,
  festivalListResponseSchema,
  SHELL_FESTIVAL_LIST_QUERY,
  type CreateFestivalRequest,
  type FestivalDto,
  type FestivalListResponse,
  type UpdateFestivalRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';

const PATH = '/festivals';

/**
 * Every festival and venue the organisation has, in the order it decided.
 *
 * `sort: 'order'` is `FESTIVAL_SORT_KEYS[0]` — the server orders by `(sort_order, name)`, which
 * is the manual ordering `festivalDtoSchema.sortOrder` documents. No `kind` parameter: the
 * sidebar wants both and groups the one answer itself, which is what the contract's own comment
 * on that field says it would do ("Absent means both").
 */
export function festivalsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.festivalList(orgId),
    queryFn: ({ signal }): Promise<FestivalListResponse> =>
      apiRequest({
        path: PATH,
        /* Den samme konstanten `/v1/me/bootstrap` spør med: svaret den legger i cachen er dette. */
        query: SHELL_FESTIVAL_LIST_QUERY,
        schema: festivalListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

/** One festival, for its own page. Detail tier: it is the subject of the screen, not a lookup. */
export function festivalQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.festival(orgId, festivalId),
    queryFn: ({ signal }): Promise<FestivalDto> =>
      apiRequest({ path: `${PATH}/${festivalId}`, schema: festivalDtoSchema, signal }),
    ...CACHE.detail,
  });
}

/**
 * The first festival an organisation has, and every one after it.
 *
 * NO `slug` IN THE REQUEST, because there is none in the contract: the API mints it once from
 * the first name and it never moves again — same rule as `departments.key`, and for the same
 * reason (a later import, a public volunteer link and the Phase 2 edition clone all address the
 * festival by it).
 */
export function useCreateFestival(
  orgId: string,
): UseMutationResult<FestivalDto, unknown, CreateFestivalRequest> {
  return useMutation({
    mutationFn: (body: CreateFestivalRequest) =>
      apiRequest({ path: PATH, method: 'POST', body, schema: festivalDtoSchema }),
    meta: { invalidates: [qk.festivals(orgId)] },
  });
}

export interface UpdateFestivalInput {
  festivalId: string;
  body: UpdateFestivalRequest;
}

/**
 * Renaming, moving, correcting the kind — everything except the slug.
 *
 * `qk.festivals(orgId)` is invalidated as a prefix rather than the detail key alone: the name
 * and the kind are both on the sidebar row, so a rename that refreshed only the page it was made
 * on would leave the old word in the menu beside it.
 */
export function useUpdateFestival(
  orgId: string,
): UseMutationResult<FestivalDto, unknown, UpdateFestivalInput> {
  return useMutation({
    mutationFn: ({ festivalId, body }: UpdateFestivalInput) =>
      apiRequest({
        path: `${PATH}/${festivalId}`,
        method: 'PATCH',
        body,
        schema: festivalDtoSchema,
      }),
    meta: { invalidates: [qk.festivals(orgId)] },
  });
}

/**
 * Faresonen (#28). 204, no body — and it takes the festival's years with it.
 *
 * THREE PREFIXES, because one call empties three lists. `qk.festivals` is the sidebar and the page
 * itself; `qk.editions` is every year that just went with it, which the switcher and the submenu
 * both read; `qk.stages` is the set that hung on the festival, and a stale one would be a list of
 * scenes belonging to something that no longer exists. Tasks, threads and performances are NOT
 * invalidated: their rows were not written, and every path to them runs through a year that is now
 * gone — sweeping them would be a round trip for an answer that has not moved.
 */
export function useDeleteFestival(orgId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    /* Festivalen og årgangenes klarhet fjernes før invalideringen — se `useDeleteEdition` (QA5 C-L12). */
    mutationFn: async (festivalId: string) => {
      await apiRequest({ path: `${PATH}/${festivalId}`, method: 'DELETE' });
      queryClient.removeQueries({ queryKey: qk.festival(orgId, festivalId) });
      queryClient.removeQueries({ queryKey: [...qk.editions(orgId), 'readiness'] });
    },
    meta: { invalidates: [qk.festivals(orgId), qk.editions(orgId), qk.stages(orgId)] },
  });
}
